import React, { useEffect } from "react";
import SeoRelatedLinks from "../../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "En dagsmeny bør være enkel å oppdatere raskt",
  },
  {
    id: "why",
    label: "Hvorfor gir en digital dagsmeny mening?",
  },
  {
    id: "use-cases",
    label: "Hva kan en digital dagsmeny brukes til?",
  },
  {
    id: "workflow",
    label: "Hvordan lager du en digital dagsmeny i praksis?",
  },
  {
    id: "content",
    label: "Hva bør en god dagsmeny inneholde?",
  },
  {
    id: "mistakes",
    label: "Hvilke feil skjer med digitale dagsmenyer?",
  },
  {
    id: "recommendation",
    label: "Hvordan ville jeg startet som restauranteier?",
  },
  {
    id: "next-steps",
    label: "Hva bør du gjøre nå?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper deg med å lage en digital dagsmeny",
  },
  {
    id: "faq",
    label: "FAQ om digitale dagsmenyer",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function CreateDigitalDailyMenuNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");
    document.title =
      "Opprett digital dagsmeny: lunsjtilbud og sesongretter | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/tageskarte-digital-erstellen",
          en: "/en/create-digital-daily-menu",
          no: "/no/opprett-digital-dagsmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Opprett en digital dagsmeny: oppdater lunsjtilbud, ukemenyer og
              sesongretter online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                En dagsmeny fungerer bare når den er aktuell. Derfor blir
                papirmenuer og PDF-er ofte upraktiske: i dag finnes det et nytt
                lunsjtilbud, i morgen er én rett utsolgt, og neste uke starter et
                sesongtilbud.
              </p>

              <p className="seoHero__text">
                En digital dagsmeny gjør slike endringer enklere. Restauranten
                kan oppdatere retter, priser, beskrivelser og bilder online,
                mens QR-koden forblir den samme.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="no" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>En dagsmeny bør være enkel å oppdatere raskt</h2>

          <p>
            En vanlig meny kan være den samme i flere måneder. En dagsmeny kan
            ikke det. Den reagerer på innkjøp, sesong, kjøkkenkapasitet,
            gjestenes etterspørsel og spontane ideer fra teamet.
          </p>

          <p>
            Derfor bør en digital dagsmeny ikke være avhengig av designer, ny
            PDF-eksport eller nytt trykkmateriale hver gang noe endres.
            Restauranten bør kunne oppdatere aktuelle tilbud raskt og samtidig
            beholde samme QR-kode.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Treg arbeidsflyt</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Dagens rett bestemmes internt.
                </li>

                <li className="text__seo-li">
                  PDF eller mal må redigeres.
                </li>

                <li className="text__seo-li">Ny fil eksporteres.</li>

                <li className="text__seo-li">
                  QR-lenke eller nettside kontrolleres.
                </li>

                <li className="text__seo-li">
                  Gjester kan fortsatt se gamle tilbud.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre arbeidsflyt</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Legg inn dagens rett i administrasjonspanelet.
                </li>

                <li className="text__seo-li">
                  Legg til pris og kort beskrivelse.
                </li>

                <li className="text__seo-li">Legg eventuelt til bilde.</li>

                <li className="text__seo-li">Lagre endringer.</li>

                <li className="text__seo-li">
                  Dagsmenyen oppdateres online.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test en liten dagsmeny først</h3>

          <p>
            Legg inn tre til fem daglige tilbud, åpne menyen på en smarttelefon
            og sjekk om arbeidsflyten er enkel nok for teamet ditt. En liten
            test er vanligvis bedre enn å bygge en stor dagsmeny før du vet
            hvordan den fungerer i daglig service.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvorfor gir en digital dagsmeny mening?</h2>

            <p>
              Dagsmenyer, lunsjtilbud og ukemenyer endres oftere enn hovedmenyen.
              Derfor trenger de en annen måte å administreres på.
            </p>

            <p>
              Hvis gjester ser et lunsjtilbud online som ikke lenger er
              tilgjengelig, blir de skuffet. Hvis et nytt tilbud ikke vises,
              mister restauranten oppmerksomhet. En digital dagsmeny hjelper med
              å unngå slike hull.
            </p>

            <h3>Dagens tilbud trenger raskere oppdateringer enn hovedmenyen</h3>

            <p>
              En restaurant kan beholde hovedrettene sine i lang tid, men
              daglige tilbud avhenger ofte av ingredienser, sesong,
              kjøkkenplanlegging og gjestenes etterspørsel. Jo enklere de er å
              oppdatere, desto mer nyttige blir de.
            </p>

            <p>
              Dette er spesielt relevant for restauranter som endrer tilbud flere
              ganger i uken eller bruker lunsjtilbud for å tiltrekke gjester i
              bestemte tidsrom.
            </p>
          </div>
        </section>

        <section id="use-cases" className="acquaintance">
          <h2>Hva kan en digital dagsmeny brukes til?</h2>

          <p>
            En digital dagsmeny er ikke bare for klassiske lunsjtilbud. Den kan
            brukes for alt innhold som endres ofte eller bare er tilgjengelig i
            en begrenset periode.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Lunsjtilbud.</li>
            <li className="text__seo-li">Dagens retter.</li>
            <li className="text__seo-li">Ukemeny.</li>
            <li className="text__seo-li">Sesongretter.</li>
            <li className="text__seo-li">Kokkens anbefalinger.</li>
            <li className="text__seo-li">Business lunch.</li>
            <li className="text__seo-li">Tidsbegrensede tilbud.</li>
            <li className="text__seo-li">Utsolgte produkter.</li>
          </ul>

          <p>
            En digital dagsmeny er spesielt nyttig når tilbud må oppdateres om
            morgenen, før lunsjservice eller i løpet av dagen.
          </p>

          <VisualIdea>
            Smarttelefon med dagsmeny: dagens anbefaling, lunsjtilbud,
            sesongrett og ett utsolgt produkt skjult fra visningen.
          </VisualIdea>
        </section>

        <section id="workflow" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvordan lager du en digital dagsmeny i praksis?</h2>

            <p>
              Arbeidsflyten bør være enkel nok til at den ikke er avhengig av en
              designer eller et eksternt byrå. Én ansvarlig person i restauranten
              bør kunne oppdatere dagsmenyen.
            </p>

            <ol className="seoSteps">
              <li className="text__seo-li">
                <strong>Opprett en kategori:</strong> For eksempel “Dagsmeny”,
                “Lunsjtilbud” eller “Dagens anbefalinger”.
              </li>

              <li className="text__seo-li">
                <strong>Legg til retter:</strong> Legg inn navn, pris og en kort
                beskrivelse.
              </li>

              <li className="text__seo-li">
                <strong>Legg eventuelt til bilde:</strong> For nye eller
                sesongbaserte retter kan et bilde hjelpe.
              </li>

              <li className="text__seo-li">
                <strong>Administrer tilgjengelighet:</strong> Skjul utsolgte
                retter eller erstatt dem.
              </li>

              <li className="text__seo-li">
                <strong>Lagre og sjekk:</strong> Åpne den offentlige menyen på
                en smarttelefon.
              </li>
            </ol>

            <p>
              QR-koden trenger ikke å trykkes på nytt hver dag. Den åpner fortsatt
              den digitale menyen, mens innholdet bak den oppdateres.
            </p>
          </div>
        </section>

        <section id="content" className="acquaintance">
          <h2>Hva bør en god dagsmeny inneholde?</h2>

          <p>
            En dagsmeny bør være kort, tydelig og enkel å forstå. Gjester vil
            ikke lete lenge. De vil se hva som er spesielt i dag.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Tydelig tittel.</li>
            <li className="text__seo-li">Kort tidsperiode.</li>
            <li className="text__seo-li">Navn på retten.</li>
            <li className="text__seo-li">Pris.</li>
            <li className="text__seo-li">Kort beskrivelse.</li>
            <li className="text__seo-li">Bilde når det er nyttig.</li>
            <li className="text__seo-li">Merknad om tilgjengelighet.</li>
            <li className="text__seo-li">Drikke- eller menyvalg.</li>
          </ul>

          <p>
            Dagsmenyer fungerer ofte best når de ikke er overfylte. Tre til sju
            sterke tilbud kan være bedre enn en lang og uklar liste.
          </p>

          <h3>En dagsmeny bør føles som en anbefaling</h3>

          <p>
            Målet er ikke å lage en ekstra full meny. Målet er å vise gjestene
            hva som er verdt å velge i dag: et lunsjtilbud, en sesongrett,
            kokkens anbefaling eller et tidsbegrenset tilbud.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hvilke feil skjer med digitale dagsmenyer?</h2>

            <p>
              En digital dagsmeny er bare nyttig når den forblir aktuell og
              tydelig. Ellers blir den bare enda en forvirrende menyside.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Vanlige feil</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Dagens retter blir liggende synlige i flere dager.
                  </li>

                  <li className="text__seo-li">
                    Utsolgte produkter skjules ikke.
                  </li>

                  <li className="text__seo-li">
                    Priser stemmer ikke med kasse eller trykt meny.
                  </li>

                  <li className="text__seo-li">
                    Dagsmenyen blir for lang.
                  </li>

                  <li className="text__seo-li">
                    Det finnes ingen tydelig tittel.
                  </li>

                  <li className="text__seo-li">
                    Bilder ser ujevne eller upassende ut.
                  </li>

                  <li className="text__seo-li">
                    Ingen har ansvar for daglige oppdateringer.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Bedre</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Merk tidsbegrensede tilbud tydelig.
                  </li>

                  <li className="text__seo-li">
                    Skjul utsolgte retter med en gang.
                  </li>

                  <li className="text__seo-li">
                    Sjekk priser sentralt.
                  </li>

                  <li className="text__seo-li">
                    Vis bare utvalgte dagstilbud.
                  </li>

                  <li className="text__seo-li">
                    Bruk tydelige titler som “Dagens anbefalinger”.
                  </li>

                  <li className="text__seo-li">
                    Velg bilder med tydelig hensikt.
                  </li>

                  <li className="text__seo-li">
                    Gi én person ansvar.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>Hvordan ville jeg startet som restauranteier?</h2>

          <p>
            Jeg ville ikke gjort dagsmenyen stor fra starten. Jeg ville startet
            med én liten seksjon som faktisk endres regelmessig.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Opprett en “I dag”-kategori:</strong> Denne kategorien
              inneholder bare aktuelle tilbud.
            </li>

            <li className="text__seo-li">
              <strong>Legg til tre til fem retter:</strong> Med pris og kort
              beskrivelse.
            </li>

            <li className="text__seo-li">
              <strong>Test ett bilde:</strong> Ikke for hver rett, men for det
              sterkeste tilbudet.
            </li>

            <li className="text__seo-li">
              <strong>Åpne QR-koden:</strong> Sjekk hvordan dagsmenyen ser ut på
              en smarttelefon.
            </li>

            <li className="text__seo-li">
              <strong>Observer i én uke:</strong> Brukes menyen? Henviser teamet
              til den?
            </li>

            <li className="text__seo-li">
              <strong>Utvid senere:</strong> Legg til ukemeny, sesongtilbud
              eller lunsjtilbud etter den første testen.
            </li>
          </ol>

          <p>
            Denne tilnærmingen holder første versjon enkel og gjør det lettere å
            forstå om en digital dagsmeny faktisk hjelper i daglig drift.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør du gjøre nå?</h2>

            <p>
              Hvis du vil lage en digital dagsmeny, start med en liten test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Opprett en digital meny.</li>

              <li className="text__seo-li">
                Legg til en “Dagsmeny”-kategori.
              </li>

              <li className="text__seo-li">
                Legg inn tre til fem ekte tilbud.
              </li>

              <li className="text__seo-li">
                Legg til priser og korte beskrivelser.
              </li>

              <li className="text__seo-li">
                Åpne menyen på en smarttelefon.
              </li>

              <li className="text__seo-li">
                Test QR-koden i restauranten.
              </li>

              <li className="text__seo-li">
                Oppdater tilbudene igjen etter én dag.
              </li>
            </ul>

            <VisualIdea>
              En enkel dagsmeny-test: én kategori, tre aktuelle tilbud, én
              QR-kode og en smarttelefonvisning som viser de oppdaterte rettene.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ om digitale dagsmenyer</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva er en digital dagsmeny?</summary>

              <p>
                Det er en online-administrert seksjon av menyen som viser dagens
                retter, lunsjtilbud, ukemenyer eller sesongbaserte produkter.
              </p>
            </details>

            <details>
              <summary>Må jeg trykke QR-koden på nytt hver dag?</summary>

              <p>
                Nei. QR-koden forblir den samme hvis den peker til en stabil
                menylenke. Bare innholdet i dagsmenyen oppdateres.
              </p>
            </details>

            <details>
              <summary>Kan jeg skjule utsolgte dagstilbud?</summary>

              <p>
                Ja. Dette er en av de mest nyttige fordelene. En rett kan skjules
                og erstattes med et annet tilbud.
              </p>
            </details>

            <details>
              <summary>Hvor mange retter bør en dagsmeny ha?</summary>

              <p>
                For en første test er tre til sju tilbud ofte nok. Dagsmenyen
                bør være tydelig og enkel å forstå.
              </p>
            </details>

            <details>
              <summary>Er en digital dagsmeny bedre enn en PDF?</summary>

              <p>
                Hvis tilbud endres ofte, er en digital dagsmeny vanligvis mer
                praktisk fordi innholdet kan oppdateres direkte.
              </p>
            </details>

            <details>
              <summary>Kan jeg starte gratis?</summary>

              <p>
                Ja. For en første test kan du lage noen dagstilbud og sjekke
                visningen via QR-kode på en smarttelefon.
              </p>
            </details>
          </div>
        </section>

        <SeoRelatedLinks
          lang="no"
          group="menu"
          exclude="/no/opprett-digital-dagsmeny"
        />
      </article>
    </main>
  );
}